/**
 * The identity service's refusal reasons in plain language
 * (pnet-identity-node access-admin.policy.ts / access-admin.service.ts /
 * access.compiler.ts). A refusal is `{success: false, message: <reason>,
 * code: <status>, ...extra}`; a 422 is `{success: false, message: {field:
 * [messages]}, code: 422}` where a message may itself be a reason code.
 */

export interface Refusal {
  /** HTTP status from the body's `code` (0 when unknown) */
  status: number;
  /** the reason code, or '' for a field-error (422) answer */
  reason: string;
  /** plain-language message for the user */
  message: string;
  /** per-field plain-language messages of a 422 */
  fields: Record<string, string>;
  extra: Record<string, unknown>;
}

/** users.type / roles.role_type codes (policy TYPE_CODE) in words. */
const TYPE_WORD: Record<string, string> = {
  '1': 'Internal',
  '2': 'Brand',
  '3': 'Right holder',
  '4': 'Agency',
  INTERNAL: 'Internal',
  BRAND: 'Brand',
  RIGHT_HOLDER: 'Right holder',
  AGENCY: 'Agency',
};
const typeWord = (v: unknown): string | null =>
  v === null || v === undefined ? null : (TYPE_WORD[String(v)] ?? null);

const list = (v: unknown): string[] => (Array.isArray(v) ? v.map(String) : []);

const DIMENSION_WORD: Record<string, string> = {
  brand: 'brand',
  partner: 'partner',
  partner_category: 'partner category',
  partner_type: 'partner type',
  country: 'country',
};

/** What may be chosen, per organisation limit (value_outside_organisation). */
const LIMIT_TEXT: Record<string, string> = {
  organisation_and_sub_brands:
    'Only the organisation’s own brand and its sub-brands can be chosen.',
  organisation_and_child_partners:
    'Only the organisation’s own partner and its child partners can be chosen.',
  agency_and_agency_sub_brands:
    'Only the agency’s own brand and its sub-brands that are agencies can be chosen.',
};

/** "1 brand" / "3 partners" of a value_outside_organisation answer. */
const valuesWord = (e: Record<string, unknown>): string => {
  const n = list(e.ids).length;
  const noun = e.dimension === 'partner' ? 'partner' : 'brand';
  return n > 0 ? `${n} ${noun}${n === 1 ? '' : 's'}` : `${noun}s`;
};

/** "Combination 3" from a 0-based rule index, or "A combination". */
const combination = (rule: unknown): string =>
  typeof rule === 'number' ? `Combination ${rule + 1}` : 'A combination';

/**
 * The no-access text (owner 2026-10-05): a user without a valid role, e.g.
 * whose only role was an old shared customer role that has been switched
 * off, sees this at sign-in (the /noaccess page) and on refusals.
 */
/** The no-access page when the user's organisation is closed (organisation_inactive). */
/**
 * use_invitation (Add user, an address with an account) for a caller who cannot invite (no
 * `invite` capability, owner 2026-10-19): nothing on the page can act on "Send an invitation
 * instead", so they ask PartnershipNet.
 */
export const ASK_PARTNERSHIPNET_TO_INVITE =
  'This person already has a PartnershipNet account. Ask PartnershipNet to invite them.';

export const ORGANISATION_INACTIVE_MESSAGE =
  'Your organisation no longer has access to PartnershipNet. Please contact your PartnershipNet account manager.';

export const NO_ACTIVE_ROLE_MESSAGE =
  'You have no active role in PNET. Your access may have been changed or switched off. Please contact your administrator.';

/** reason -> message; `extra` carries rule index, permission names, seat limit. */
const MESSAGES: Record<string, (extra: Record<string, unknown>) => string> = {
  // 403: permissions and tenant
  permission_required: (e) =>
    e.permission
      ? `Your active role does not have the permission "${String(e.permission)}".`
      : 'Your active role does not have the permission for this action.',
  users_self_service_disabled: () =>
    'User administration is switched off for your organisation. Please ask PartnershipNet support.',
  user_outside_tenant: () => 'This user is outside your organisation and cannot be managed by you.',
  context_outside_tenant: () => 'You can only grant roles inside your own organisation.',
  role_not_assignable: () => 'This role cannot be assigned in this organisation.',
  internal_role_not_assignable: () => 'Internal roles cannot be assigned by an organisation.',
  scope_outside_ceiling: (e) => {
    const rule = typeof e.rule === 'number' ? ` Combination ${e.rule + 1}` : ' A combination';
    return `${rule.trim()} goes beyond your own access: you can only grant brands and partners of your own organisation.`;
  },
  // 422: partners by name OR by category / partner type (owner rule 2026-10-05)
  partner_selection_conflict: (e) =>
    e.partner_mode === 'NONE'
      ? `${combination(e.rule)} has Partners set to None together with a category or partner type. To filter partners by category and partner type, set Partners to All.`
      : `${combination(e.rule)} chooses partners both by name and by category or partner type. Choose one way per combination; need both? Add another combination.`,
  // 422: own dimension required in customer roles (owner rule 2026-10-05)
  own_dimension_required: (e) => {
    const d = String(e.dimension ?? 'brand');
    const plural = d === 'partner' ? 'partners' : 'brands';
    const who = d === 'partner' ? 'A Right holder' : 'A Brand or Agency';
    return `${combination(e.rule)}: choose the ${plural}. ${who} role must choose its ${plural}; All is not allowed.`;
  },
  unknown_ids: (e) =>
    `Some ${DIMENSION_WORD[String(e.dimension)] ?? 'value'} entries no longer exist (${list(e.ids).join(', ') || 'unknown'}). Remove them from the combinations.`,
  // 403: platform-owner only
  admin_role_requires_platform_owner: () => 'Only a Super Admin can grant an administrator role.',
  privileged_requires_platform_owner: () => 'Only a Super Admin can grant privileged access.',
  internal_context_requires_platform_owner: () =>
    'Only a Super Admin can grant roles in the Internal organisation.',
  platform_owner_required: () => 'Only a Super Admin can do this.',
  // 403: self administration
  self_administration_not_allowed: () =>
    'You cannot administer your own user record here. Use "My profile" for your own details.',
  self_status_change_not_allowed: () =>
    'You cannot change your own status or disable or re-enable your own role assignments.',
  self_email_change_not_allowed: () => 'You cannot change your own e-mail address.',
  self_privilege_change_not_allowed: () =>
    'You cannot change the privilege level of your own role assignments.',
  self_validity_change_not_allowed: () =>
    'You cannot change the validity of your own role assignments, or give yourself access beyond the end of your current role.',
  self_escalation_not_allowed: (e) =>
    Array.isArray(e.permissions) && e.permissions.length > 0
      ? `You cannot give yourself a role with permissions you do not have: ${e.permissions.join(', ')}.`
      : 'You cannot give yourself more access than you have.',
  self_mfa_reset_not_allowed: () => 'You cannot reset your own MFA here. Use "Security" instead.',
  // user type (the person's, §13.9)
  assignment_type_mismatch: (e) => {
    const t = typeWord(e.user_type);
    return t && t !== 'Internal'
      ? `This role does not fit the user's type: ${t} users only get ${t} roles in ${t.toLowerCase()} organisations.`
      : "This role or organisation does not fit the user's type.";
  },
  user_type_not_allowed: () => 'You can only add users of your own organisation’s type.',
  // one organisation per customer user (owner rule 2026-10-05)
  organisation_required: () =>
    'This user has no organisation yet. Brand, Right holder and Agency users belong to one organisation: ask a Super Admin to correct the home organisation on the user page.',
  organisation_type_mismatch: (e) => {
    if (e.is_agency_brand === true)
      return 'This brand is an agency. Choose it as an Agency organisation, not as a Brand.';
    if (e.is_agency_brand === false)
      return 'This brand is not an agency. Choose it as a Brand organisation.';
    const t = typeWord(e.user_type);
    if (t === 'Internal') return 'Internal users do not belong to an organisation.';
    return t
      ? `This organisation does not fit the user: ${t} users belong to a ${t.toLowerCase()} organisation.`
      : 'This organisation does not fit the user’s type.';
  },
  organisation_has_no_roles: () =>
    'This organisation has no active role yet, so its users could not get any access. Create a role for it first (Settings > Roles).',
  assignment_outside_organisation: () =>
    'This role is in another organisation. Brand, Right holder and Agency users only get roles in their own organisation.',
  // "Correct home organisation" (owner 2026-10-31): replaces Change type and Change organisation
  use_correct_home: () =>
    'A person’s type and organisation are no longer changed here. A Super Admin uses “Correct home organisation” on the person’s page.',
  // the server's own-record refusal; the page hides the button on one's own record
  self_home_correction_not_allowed: () => 'You cannot correct your own home organisation.',
  user_has_running_roles: (e) => {
    const n = list(e.assignments).length;
    const roles = n > 0 ? `${n} running role${n === 1 ? '' : 's'}` : 'running roles';
    return `The person still holds ${roles}. End ${n === 1 ? 'it' : 'them'} first (End access in a client organisation, Disable in the Internal organisation), then correct the home organisation.`;
  },
  organisation_not_available: () =>
    'This organisation cannot be the person’s home: choose a brand, right holder or agency that is not a former client.',
  value_outside_organisation: (e) =>
    `${combination(e.rule)} names ${valuesWord(e)} outside the user’s organisation. ${LIMIT_TEXT[String(e.limit)] ?? 'Only values of the organisation itself can be chosen.'} Remove ${list(e.ids).length === 1 ? 'it' : 'them'}.`,
  // role administration (Settings > Roles)
  roles_self_service_off: () =>
    'Role administration is switched off for your organisation. Please ask PartnershipNet support.',
  role_outside_tenant: () => 'This role belongs to another organisation.',
  role_permission_not_held: (e) =>
    list(e.permissions).length > 0
      ? `You can only give a role permissions you hold yourself. Not held: ${list(e.permissions).join(', ')}.`
      : 'You can only give a role permissions you hold yourself.',
  role_application_not_enabled: () =>
    'Your organisation does not have this application, so a role for it cannot be created.',
  role_requires_platform_owner: (e) =>
    list(e.permissions).length > 0
      ? `Only a Super Admin can create or change roles with these permissions: ${list(e.permissions).join(', ')}.`
      : 'Only a Super Admin can create or change this role.',
  role_requires_organisation: (e) => {
    const t = typeWord(e.role_type);
    const kind = t ? t.toLowerCase() : 'organisation';
    return t
      ? `A ${t} role must belong to one ${kind}: choose the ${kind} it is for.`
      : 'This role must belong to one organisation: choose it.';
  },
  role_tenant_type_mismatch: () =>
    "The role's type does not match the type of the organisation that owns it.",
  system_role: (e) => {
    const fields = list(e.fields);
    if (list(e.permission_ids).length > 0)
      return 'This is a system role: it holds every permission, so none can be removed.';
    if (fields.length > 0)
      return `This is a system role: its name, type, organisation and active state cannot change (${fields.join(', ')}).`;
    return 'This is a system role: it cannot be deleted or deactivated.';
  },
  role_in_use: (e) =>
    typeof e.assignments === 'number'
      ? `This role is still assigned to ${e.assignments} user${e.assignments === 1 ? '' : 's'}. Remove it from them first.`
      : 'This role is assigned to users: its type and organisation cannot change and it cannot be deleted.',
  // 409
  seat_limit_reached: (e) =>
    typeof e.seat_limit === 'number'
      ? `The organisation has used all its seats (${e.seat_limit}). Deactivate a user or ask PartnershipNet to raise the limit.`
      : 'The organisation has used all its seats.',
  // Add user creates PartnershipNet staff only (owner 2026-10-31); an address with an account, or
  // a person of a client organisation, is invited (owner 2026-10-16)
  use_invitation: () =>
    'This person cannot be added as a new user here. People of a client organisation, and addresses that already have an account, are invited. Send an invitation instead.',
  // the old role routes, for a client organisation's role (owner 2026-10-31)
  use_membership_routes: () =>
    'Roles in a client organisation change only through the organisation: Renew, Change or End access on the role, or a new invitation.',
  // switching off the last administrator's account (owner 2026-10-31)
  last_admin: () =>
    'This person is the only active administrator of their organisation. Add another administrator first.',
  // the invitation's limits (owner 2026-10-16)
  duration_too_long: (e) =>
    `A role you give can last at most ${typeof e.max_months === 'number' ? e.max_months : 12} months.`,
  external_needs_home_account: () =>
    'This address belongs to another organisation that is a client: that organisation adds the person first, then you can invite them.',
  assignment_exists: () =>
    'The user already has this role in this organisation. Edit or re-enable that assignment instead.',
  role_changed: () => 'Your role changed in another tab. Reload the page to continue.',
  // caller state (resolver)
  assignment_required: () => 'Choose your active role first (role menu in the sidebar).',
  no_assignment: () => NO_ACTIVE_ROLE_MESSAGE,
  assignment_not_valid: () => 'Your active role is no longer valid. Choose another role.',
  user_inactive: () => 'Your account is not active.',
  unknown_user: () => 'Your session is not valid. Please sign in again.',
  // 404
  user_not_found: () => 'User not found (or not visible to you).',
  assignment_not_found: () => 'Role assignment not found.',
  // 422 reasons (as messages of a field)
  role_not_found: () => 'The role does not exist.',
  role_inactive: () => 'The role is not active.',
  role_not_assignable_in_context: () =>
    'This role does not belong to the chosen organisation type or organisation.',
  context_not_found: () => 'The organisation was not found.',
  context_inactive: () => 'The organisation is not active.',
  privileged_requires_internal_context: () =>
    'Privileged access can only be granted in the Internal organisation.',
  reason_required: () => 'A reason is required.',
  internal_requires_company_domain: () =>
    'Internal roles can only be given to PartnershipNet staff (e-mail on partnership-net.com or advanced-sponsorship-insights.com).',
  // security rules (Settings > Manage Users > Rules, §13.16)
  security_rules_internal_only: () =>
    'Security rules are managed by PartnershipNet staff with the Application Admin role (the permission to manage security rules).',
  security_rule_not_found: () => 'This security rule does not exist (any more).',
  super_admin_required: () => 'Only Super Admins manage the security rules.',
  invalid_mode: () => 'Choose Disabled, Notify or Enforced.',
  invalid_params: () => 'Some settings of this rule are not valid.',
  run_in_progress: () => 'The security rules are running right now. Try again in a few minutes.',
  self_approval_not_allowed: () =>
    'You cannot approve your own request, or a request for yourself. Another person has to decide it.',
  sso_required: () => 'PartnershipNet staff sign in with single sign-on (SSO) only.',
  // gateway /sso/* for a customer organisation (owner decision 2026-10-07)
  sso_settings_forbidden: () =>
    'Only Super Admins and this organisation’s administrators can change its sign-in settings.',
  mode_not_allowed: () => 'This rule cannot be set to that mode.',
  notify_recipient_required: () => 'Choose who is notified: the user, admins or both.',
  rule_unavailable: () => 'This rule is not available right now.',
  privileged_requires_end_date: (e) => {
    const n = Number(e.max_months ?? e.months);
    return Number.isFinite(n) && n > 0
      ? `A privileged role needs an end date of at most ${n} month${n === 1 ? '' : 's'} (security rule).`
      : 'A privileged role needs an end date (security rule).';
  },
  access_review_not_found: () => 'This access review does not exist (any more).',
  access_review_not_open: () => 'This access review has already been confirmed or closed.',
  // client status of organisations (owner decision 2026-10-07)
  organisation_inactive: (e) =>
    e.client_status === 'former'
      ? 'This organisation is a former client: its users can no longer sign in, and no users or roles can be added.'
      : 'This organisation no longer has access to PartnershipNet (former client or ended trial).',
  organisation_prospect: () =>
    'This organisation is a prospect: no customer users can be created or given roles in it. Change its status under Settings > Manage Users > Organisations first.',
  organisation_not_found: () =>
    'This organisation has not been set up yet. A Super Admin or Application Admin creates it under Settings > Manage Users > Organisations.',
  organisation_admin_required: () =>
    'Only Super Admins and PartnershipNet staff with the Application Admin role can create organisations or change their client status.',
  // the direct status change (owner 2026-10-31): through the Set-up, with a second person
  use_setup_change: () =>
    'A client status is changed in the organisation’s Set-up (Settings > Manage Users > Organisations), where a Super Admin approves it.',
  organisation_exists: () => 'This organisation already exists.',
  internal_has_no_client_status: () => 'The Internal organisation has no client status.',
  trial_ends_at_required: () => 'A trial needs an end date in the future.',
  client_status_unavailable: () =>
    'Client statuses are not available yet (database update pending). Only “Client” can be used.',
  // 410: the old user screens' endpoints
  moved_to_admin_api: () =>
    'This old user-administration function has been replaced. Please use Settings > All Users.',
  identity_api_not_configured: () => 'The identity service is not configured for this site.',
  // role preview (Authorization-Plan §13.7, owner 2026-10-07)
  preview_read_only: () => 'Preview is read only',
  internal_cross_organisation_not_allowed: () =>
    'PartnershipNet staff join a customer organisation only as a guest, by invitation. Use "Preview as this role" to work in PNET as this role (changes are saved and recorded under your own name).',
  preview_not_allowed: () =>
    'Previewing roles needs an active Internal role with the permission to preview roles (Application Admin).',
  role_not_previewable: () =>
    'This role cannot be previewed: it does not belong to exactly one organisation.',
  preview_context_mismatch: () => 'This role belongs to another organisation.',
  preview_public_only_not_available: () =>
    'This organisation is a prospect: previewing it (public data only) is not available yet.',
  preview_not_allowed_for_status: (e) =>
    e.client_status
      ? `This organisation cannot be previewed (status: ${String(e.client_status)}).`
      : 'This organisation cannot be previewed in its current status.',
  preview_not_active: () => 'There is no active preview (it may have ended).',
  preview_max_duration_reached: () =>
    'A preview lasts at most 2 hours from its start; it cannot be extended further.',
  role_preview_unavailable: () => 'Role preview is not available yet on this environment.',
};

export const KNOWN_REASONS = Object.keys(MESSAGES);

// ---- the pre-save check: warnings (Save allowed) ----------------------------------------------

const FIXED_LIST_WORD: Record<string, string> = {
  country: 'countries',
  partner_category: 'categories',
  partner_type: 'partner types',
};

/** "no <plural>" of rule_matches_nothing. */
const PLURAL_WORD: Record<string, string> = {
  brand: 'brands',
  partner: 'partners',
  partner_category: 'categories',
  partner_type: 'partner types',
  country: 'countries',
};

/** "a, b and c" / "a, b or c". */
export function joinWords(words: string[], last: 'and' | 'or' = 'and'): string {
  if (words.length <= 1) return words.join('');
  return `${words.slice(0, -1).join(', ')} ${last} ${words[words.length - 1]}`;
}

const capitalise = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

const numbers = (v: unknown): number[] =>
  Array.isArray(v) ? v.filter((n): n is number => typeof n === 'number') : [];

/**
 * 0-based rule indexes as "combination 2" / "combinations 1–4" /
 * "combinations 1, 3 and 5" (runs of three or more as a range).
 */
export function combinationsLabel(rules: number[]): string {
  const sorted = [...new Set(rules)].sort((a, b) => a - b).map((n) => n + 1);
  if (sorted.length === 1) return `combination ${sorted[0]}`;
  const parts: string[] = [];
  for (let i = 0; i < sorted.length; ) {
    let j = i;
    while (j + 1 < sorted.length && sorted[j + 1] === sorted[j] + 1) j++;
    if (j - i >= 2) parts.push(`${sorted[i]}–${sorted[j]}`);
    else for (let k = i; k <= j; k++) parts.push(String(sorted[k]));
    i = j + 1;
  }
  return `combinations ${joinWords(parts)}`;
}

/** First names of a list, then "and N more". */
const someNames = (names: string[], max = 3): string =>
  names.length > max
    ? `${names.slice(0, max).join(', ')} and ${names.length - max} more`
    : joinWords(names);

/** A warning's text from its message_params (+ `rule`, the combination index). */
const WARNINGS: typeof MESSAGES = {
  category_excludes_uncategorised: (p) =>
    typeof p.partners === 'number'
      ? `Partners without a category (${p.partners.toLocaleString('en')} today) are never included when you filter by category.`
      : 'Partners without a category are never included when you filter by category.',
  partner_multi_category: () =>
    'Partners with several categories are included if any of their categories matches.',
  rule_matches_nothing: (p) => {
    const dims = list(p.dimensions).map((d) => PLURAL_WORD[d] ?? d);
    return `${combination(p.rule)} gives no access: it allows no ${dims.length > 0 ? joinWords(dims, 'or') : 'data'}. Remove it or choose values.`;
  },
  rule_redundant: (p) =>
    `${combination(p.rule)} is already covered by ${combination(p.contained_in).toLowerCase()}; you can remove it.`,
  fixed_list: (p) => {
    const dims = list(p.dimensions).filter((d) => FIXED_LIST_WORD[d]);
    const byDimension = isRecord(p.rules_by_dimension) ? p.rules_by_dimension : {};
    const at = (d: string): string => {
      const rules = numbers(byDimension[d]);
      return rules.length > 0 ? ` (${combinationsLabel(rules)})` : '';
    };
    const later = (nouns: string) =>
      `${capitalise(nouns)} added to PNET later are not included automatically; edit the role to add them.`;
    if (dims.length === 0)
      return `The values you picked are saved as a fixed list. ${later('values')}`;
    const nouns = joinWords(dims.map((d) => FIXED_LIST_WORD[d]));
    if (dims.length === 1)
      return `The ${nouns} you picked${at(dims[0])} are saved as a fixed list. ${later(nouns)}`;
    return `The ${joinWords(dims.map((d) => `${FIXED_LIST_WORD[d]}${at(d)}`))} you picked are saved as fixed lists. ${later(nouns)}`;
  },
  children_redundant: (p) => {
    const brand = p.dimension === 'brand';
    const names = list(p.names);
    const n = names.length > 0 ? names.length : list(p.ids).length;
    const what =
      names.length > 0
        ? someNames(names)
        : `${n} chosen ${brand ? (n === 1 ? 'sub-brand' : 'sub-brands') : n === 1 ? 'child partner' : 'child partners'}`;
    const parent =
      typeof p.parent_name === 'string' && p.parent_name !== ''
        ? p.parent_name
        : `${n === 1 ? 'its' : 'their'} parent`;
    const where = typeof p.rule === 'number' ? `In combination ${p.rule + 1}` : 'In a combination';
    return `${where}, ${what} ${n === 1 ? 'is' : 'are'} already included through ${parent} (incl. ${brand ? 'sub-brands' : 'child partners'}).`;
  },
  grants_admin_permissions: () =>
    'This role can manage users or roles. Give it only to people who administer access.',
  no_active_role_after: () => 'After this change the user has no active role and cannot use PNET.',
};

/** Plain-language text of one error or warning of the pre-save check. */
export function checkIssueMessage(issue: {
  code: string;
  rule?: number;
  dimension?: string;
  message_params?: Record<string, unknown>;
}): string {
  const p = issue.message_params ?? {};
  if (WARNINGS[issue.code]) return WARNINGS[issue.code]({ ...p, rule: issue.rule });
  if (issue.code === 'invalid_input') {
    const field = String(p.field ?? '');
    const text = list(p.messages)
      .map((m) => reasonMessage(m, p))
      .join(' ');
    return field ? `${fieldLabel(field)}: ${text}` : text;
  }
  const extra = {
    ...p,
    ...(issue.rule !== undefined ? { rule: issue.rule } : {}),
    ...(issue.dimension !== undefined ? { dimension: issue.dimension } : {}),
  };
  const text = reasonMessage(issue.code, extra);
  if (p.field && typeof p.field === 'string' && text !== issue.code)
    return `${fieldLabel(p.field)}: ${text}`;
  return text;
}

/**
 * The note under one row of the combinations table while editing (the row
 * number is already there): "Already covered by combination 1; it has no
 * effect." for rule_redundant, a short text for the own-dimension and
 * partner-choice errors, else the full message.
 */
export function rowIssueMessage(issue: {
  code: string;
  rule?: number;
  dimension?: string;
  message_params?: Record<string, unknown>;
}): string {
  const p = issue.message_params ?? {};
  if (issue.code === 'rule_redundant')
    return typeof p.contained_in === 'number'
      ? `Already covered by combination ${p.contained_in + 1}; it has no effect.`
      : 'Already covered by another combination; it has no effect.';
  if (issue.code === 'own_dimension_required') {
    const plural = issue.dimension === 'partner' ? 'partners' : 'brands';
    const who = issue.dimension === 'partner' ? 'A Right holder' : 'A Brand or Agency';
    return `Choose the ${plural}: ${who.charAt(0).toLowerCase()}${who.slice(1)} role must choose its ${plural} (All is not allowed).`;
  }
  if (issue.code === 'value_outside_organisation') {
    const extra = { ...p, dimension: issue.dimension ?? p.dimension };
    const one = list(p.ids).length === 1;
    return `${capitalise(valuesWord(extra))} ${one ? 'is' : 'are'} outside the user’s organisation. ${LIMIT_TEXT[String(p.limit)] ?? ''}`.trim();
  }
  if (issue.code === 'partner_selection_conflict')
    return p.partner_mode === 'NONE'
      ? 'Partners is None together with a category or partner type: set Partners to All to filter by category and partner type.'
      : 'Choose partners by name OR by category and partner type, not both. Need both? Add another combination.';
  return checkIssueMessage(issue);
}

/** Plain-language text of one reason code; unknown text is returned as it is. */
export function reasonMessage(reason: string, extra: Record<string, unknown> = {}): string {
  const fn = MESSAGES[reason];
  return fn ? fn(extra) : reason;
}

const FIELD_LABEL: Record<string, string> = {
  role_id: 'Role',
  context_id: 'Organisation',
  context: 'Organisation',
  'context.type': 'Organisation type',
  valid_from: 'Valid from',
  valid_until: 'Valid until',
  privilege_level: 'Privilege level',
  reason: 'Reason',
  rules: 'Access combinations',
  email: 'E-mail',
  first_name: 'First name',
  last_name: 'Last name',
  status: 'Status',
  type: 'User type',
  user_type: 'User type',
  organisation: 'Organisation',
  dimension: 'Column',
  // Settings > Roles (Role/Store, Role/Update)
  name: 'Name',
  role_type: 'Type',
  description: 'Description',
  application_ids: 'Application',
  applications_ids: 'Application',
  brand_id: 'Organisation',
  property_id: 'Organisation',
  agency_id: 'Organisation',
  permission_id: 'Permissions',
  // security rules
  mode: 'Mode',
  params: 'Settings',
  'params.days': 'Disable after (days)',
  'params.warn_days': 'Warn (days before)',
  days: 'Disable after (days)',
  warn_days: 'Warn (days before)',
  notify_user: 'Notify the user',
  notify_admins: 'Notify admins',
};

/** `rules.0.country.ids` -> "Combination 1, country"; known fields by label. */
export function fieldLabel(field: string): string {
  if (FIELD_LABEL[field]) return FIELD_LABEL[field];
  const m = /^rules\.(\d+)(?:\.([a-z_]+))?/.exec(field);
  if (m) {
    const dim = m[2] ? DIMENSION_WORD[m[2]] : undefined;
    return dim ? `Combination ${Number(m[1]) + 1}, ${dim}` : `Combination ${Number(m[1]) + 1}`;
  }
  const d = /^rules\.([a-z_]+)$/.exec(field);
  if (d && DIMENSION_WORD[d[1]]) return `Access combinations, ${DIMENSION_WORD[d[1]]}`;
  return field;
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * A refusal from an identity answer, or null when the answer is a success.
 * Works for 403/404/409 reason answers and 422 field answers.
 */
export function toRefusal(payload: unknown): Refusal | null {
  if (!isRecord(payload)) {
    return {
      status: 0,
      reason: 'network',
      message: 'The identity service did not answer. Please try again.',
      fields: {},
      extra: {},
    };
  }
  if (payload.success === true) return null;
  const status = Number(payload.code ?? payload.status ?? 0) || 0;
  const { success: _s, message, code: _c, status: _st, ...extra } = payload;
  void _s;
  void _c;
  void _st;
  if (isRecord(message)) {
    const fields: Record<string, string> = {};
    for (const [field, value] of Object.entries(message)) {
      const list = Array.isArray(value) ? value : [value];
      fields[field] = list.map((m) => reasonMessage(String(m), extra)).join(' ');
    }
    const summary = Object.entries(fields)
      .map(([field, text]) => `${fieldLabel(field)}: ${text}`)
      .join(' ');
    return { status: status || 422, reason: '', message: summary, fields, extra };
  }
  const reason = String(message ?? '').trim() || 'request_failed';
  return {
    status,
    reason,
    message:
      reason === 'request_failed'
        ? 'The request failed. Please try again.'
        : reasonMessage(reason, extra),
    fields: {},
    extra,
  };
}

/**
 * For the old screens that show `response.message` themselves (Settings >
 * Roles): a known reason code becomes its plain-language text (with the
 * answer's extra fields); anything else (422 field objects, free text) is
 * returned unchanged.
 */
export function plainApiMessage<T>(
  response: { message?: T } & Record<string, unknown>,
): T | string {
  const m = response?.message;
  if (typeof m !== 'string' || !MESSAGES[m]) return m as T;
  const { success: _s, message: _m, code: _c, status: _st, ...extra } = response;
  void _s;
  void _m;
  void _c;
  void _st;
  return reasonMessage(m, extra);
}
